import { createSelector } from 'reselect';

import { IAuthenticationDataState } from '../../states/dataState';
import { ICredentials, IAuthenticationResult, ILoginResult, IUpdatePassword } from '../../../authentication/types';
import { IAccount, IMfaAuthSetup, IMfaAuthCodeType } from '../../../authentication/types';

import * as authentication from '../../actions/data/authentication';
import * as _ from 'lodash';
import { IMessage, MessageType } from '../../../shared/types';

const initialState = {
    isAuthenticated: false,
    isNewRegisterUser: null,
    isUserStatusChanged: null,
    jwtToken: '',
    status: null,
    loading: false,
    account: null,
    isMFAEnabled: false,
    isMFAAuthenticated: false,
    MFAAuthSetup: null,
    MFAAuthCodeType: null
};

export function authenticationReducer(state: IAuthenticationDataState = initialState,  action: authentication.Actions): IAuthenticationDataState {
    switch (action.type) {
      case authentication.ActionTypes.SERVER_LOGIN:
      case authentication.ActionTypes.SERVER_LOGOUT:
      case authentication.ActionTypes.SERVER_RESET_PASSWORD:
        return Object.assign({}, state, { loading: true, status: null });
      case authentication.ActionTypes.SERVER_LOGIN_SUCCESS:
        const authenticationResult: ILoginResult = action.payload;
        let _isAuthenticated = false;

        //isAuthenticated is set to true in 3 cases
        //Case1: MFA is not enabled and not first time user => Set to true
        //Case2: MFA is not enabled and first time user => Then set to true on update of password.
        //Case3: MFA is enabled => Then on verification of mfa code 
        if ((authenticationResult.lastlogin && !authenticationResult.MFAenabled) || (authenticationResult.lastlogin && authenticationResult.MFAenabled && authenticationResult.MFAAuthenticated)) {
          _isAuthenticated = true;
        }

        return {
            isAuthenticated: _isAuthenticated,
            isNewRegisterUser: _.isNil(authenticationResult.lastlogin),
            isUserStatusChanged: false,
            jwtToken: authenticationResult.token,
            status: null,
            loading: false,
            account: {
                userID: authenticationResult.userID,
                starterFirstName: authenticationResult.starterFirstName,
                starterSurname: authenticationResult.starterSurname,  
                offboardeeFirstName: authenticationResult.offboardeeFirstName,
                offboardeeSurname: authenticationResult.offboardeeSurname,  
                starterID: authenticationResult.starterID,
                inducteeID: authenticationResult.inducteeID,
                offboardeeID: authenticationResult.offboardeeID,
                lastlogin: authenticationResult.lastlogin,
                username: (!_.isNil(authenticationResult.username))? authenticationResult.username : '',
                password: (_.isNil(authenticationResult.lastlogin) || (authenticationResult.MFAenabled && !authenticationResult.MFAAuthenticated)) ? (authenticationResult.password || '') : '',
                subPortal: !_.isNil(authenticationResult.subPortal) ? authenticationResult.subPortal : '',
                starters: !_.isNil(authenticationResult.starters) ? authenticationResult.starters : '',
                offboardees: !_.isNil(authenticationResult.offboardees) ? authenticationResult.offboardees : '',
                welcomemessage: authenticationResult.welcomemessage
                // starterJobTitle:  !_.isNil(authenticationResult.starterJobTitle) ? authenticationResult.starterJobTitle : '',
            },
            isMFAEnabled: authenticationResult.MFAenabled,
            isMFAAuthenticated: authenticationResult.MFAAuthenticated,
            MFAAuthSetup: {
              MFAAuthKey: authenticationResult.MFAAuthKey,
              MFAQRCodeLink: authenticationResult.MFAQRCodeLink,
              MFAFailCount: authenticationResult.MFAFailCount,
              MFASetupComplete: authenticationResult.MFASetupComplete
            },
            MFAAuthCodeType: {
              MFAAuthenticatorOTP: authenticationResult.MFAAuthenticatorOTP,
              MFAEmailOTP: authenticationResult.MFAEmailOTP
            }

        }; 
        case authentication.ActionTypes.SERVER_LOGIN_FAIL:
        case authentication.ActionTypes.SERVER_RESET_PASSWORD_FAIL:
        case authentication.ActionTypes.SERVER_UPDATE_PASSWORD_FAIL:
        case authentication.ActionTypes.SERVER_MFA_LOGIN_FAIL:
        case authentication.ActionTypes.SAVE_MFA_AUTH_TYPE_FAIL:
        case authentication.ActionTypes.RESEND_MFA_CODE_FAIL:
          return Object.assign({}, state, {
            status: {
              type: MessageType.ERROR,
              message: action.payload['message']
            },
            loading: false
          });
        case authentication.ActionTypes.SERVER_LOGOUT_SUCCESS:
          return {
              isAuthenticated: false,
              isNewRegisterUser: null,
              isUserStatusChanged: null,
              jwtToken: null,
              status: null,
              loading: false,
              account: null,
              isMFAEnabled: false,
              isMFAAuthenticated: false,
              MFAAuthSetup: null,
              MFAAuthCodeType: null
          };
        case authentication.ActionTypes.SERVER_RESET_PASSWORD_SUCCESS:
          const resetPasswordResult: IAuthenticationResult = action.payload;
          return {
              isAuthenticated: false,
              isNewRegisterUser: null,
              isUserStatusChanged: null,
              jwtToken: null,
              status: {
                type: MessageType.INFO,
                message: resetPasswordResult.message
              },
              loading: false,
              account: null,
              isMFAEnabled: false,
              isMFAAuthenticated: false,
              MFAAuthSetup: null,
              MFAAuthCodeType: null
          };
        case authentication.ActionTypes.SERVER_UPDATE_PASSWORD:
          return Object.assign({}, state, {
            isUserStatusChanged: false,
            status: null,
            loading: true
          });
        case authentication.ActionTypes.SERVER_UPDATE_PASSWORD_SUCCESS:
          const updatePasswordResult: IAuthenticationResult = action.payload;
          let _isNewRegisterUser = true;
          let _isUserStatusChanged = false;
          let isUserAuthenticated = false;
          // After login successfully
          // state.isNewRegisterUser always has value of TRUE or FALSE
          // If TRUE
          // we check whether updatePasswordResult has value lastLogin to update isNewRegisterUser status

          if (state.isNewRegisterUser) {
            if ( updatePasswordResult['lastlogin'] ) {
              _isNewRegisterUser = _.isNil(updatePasswordResult['lastlogin']);
              _isUserStatusChanged = true;
              
              //If the user has successfully updated the password & MFA is not enabled set "isAuthenticated" to true.
              //If the user has successfully updated the password & MFA is enabled then "isAuthenticated" is set to true when mfa code is validated successfully.This will be done in MFA Success action
              if (!_isNewRegisterUser && !state.isMFAEnabled) {
                isUserAuthenticated = !_isNewRegisterUser
              }

            }
          } else { // If FALSE, ie: existing user, we dont need to change user status
            _isNewRegisterUser = state.isNewRegisterUser;
            isUserAuthenticated = state.isAuthenticated;
          }

          return Object.assign({}, state, {
            isAuthenticated: isUserAuthenticated,
            isNewRegisterUser: _isNewRegisterUser,
            jwtToken: updatePasswordResult.token,
            isUserStatusChanged: _isUserStatusChanged,
            status: {
              type: MessageType.INFO,
              message: updatePasswordResult.message
            },
            loading: false,
            account: {
              userID: state.account.userID,
              starterID: state.account.starterID,
              lastlogin: updatePasswordResult.lastlogin,
              username: (!isUserAuthenticated)? state.account.username : '',
              password: (!isUserAuthenticated)? updatePasswordResult.password : '',
              subPortal: state.account.subPortal || '',
                starters: state.account.starters,
                // starterJobTitle: state.account.starterJobTitle,
            }
          });
        
        case authentication.ActionTypes.SERVER_MFA_LOGIN:
          return Object.assign({}, state, {
            isAuthenticated: false,
            status: null,
            loading: true,
            isMFAAuthenticated: false,
          });
        
        case authentication.ActionTypes.SERVER_MFA_LOGIN_SUCCESS:
          const mfaAuthenticationResult: ILoginResult = action.payload;

          return Object.assign({}, state, {
            isAuthenticated: mfaAuthenticationResult.MFAAuthenticated,
            isNewRegisterUser: false,
            isUserStatusChanged: false,
            jwtToken: mfaAuthenticationResult.token,
            status: null,
            loading: false,
            account: {
              userID: mfaAuthenticationResult.userID,
              starterFirstName: mfaAuthenticationResult.starterFirstName,
              starterSurname: mfaAuthenticationResult.starterSurname,  
              offboardeeFirstName: mfaAuthenticationResult.offboardeeFirstName,
              offboardeeSurname: mfaAuthenticationResult.offboardeeSurname,  
              starterID: mfaAuthenticationResult.starterID,
              inducteeID: mfaAuthenticationResult.inducteeID,
              offboardeeID: mfaAuthenticationResult.offboardeeID,
              lastlogin: mfaAuthenticationResult.lastlogin,
              username: '',
              password: '',
              subPortal: !_.isNil(mfaAuthenticationResult.subPortal) ? mfaAuthenticationResult.subPortal : '',
              starters: !_.isNil(mfaAuthenticationResult.starters) ? mfaAuthenticationResult.starters : '',
              offboardees: !_.isNil(mfaAuthenticationResult.offboardees) ? mfaAuthenticationResult.offboardees : '',
              welcomemessage: mfaAuthenticationResult.welcomemessage
              // starterJobTitle:  !_.isNil(authenticationResult.starterJobTitle) ? authenticationResult.starterJobTitle : '',
            },
            isMFAEnabled: mfaAuthenticationResult.MFAenabled,
            isMFAAuthenticated: mfaAuthenticationResult.MFAAuthenticated,
            MFAAuthSetup: {
              MFAAuthKey: mfaAuthenticationResult.MFAAuthKey,
              MFAQRCodeLink: mfaAuthenticationResult.MFAQRCodeLink,
              MFAFailCount: mfaAuthenticationResult.MFAFailCount,
              MFASetupComplete: mfaAuthenticationResult.MFASetupComplete
            }
          });

        case authentication.ActionTypes.SAVE_MFA_AUTH_TYPE:
          return Object.assign({}, state, {
            status: null,
            loading: true,
            MFAAuthCodeType: {
              MFAAuthenticatorOTP: false,
              MFAEmailOTP: false
            }
          });
        
        case authentication.ActionTypes.SAVE_MFA_AUTH_TYPE_SUCCESS:
          const mfaAuthTypeResult: any = action.payload;

          return Object.assign({}, state, {
            status: {
              type: MessageType.INFO,
              message: mfaAuthTypeResult.message
            },
            loading: false,
            MFAAuthCodeType: {
              MFAAuthenticatorOTP: mfaAuthTypeResult.MFAAuthenticatorOTP,
              MFAEmailOTP: mfaAuthTypeResult.MFAEmailOTP
            }
          });

        case authentication.ActionTypes.RESEND_MFA_CODE:
          return Object.assign({}, state, {
            status: null,
            loading: true
          });

        case authentication.ActionTypes.RESEND_MFA_CODE_SUCCESS:
          return Object.assign({}, state, {
            status: {
              type: MessageType.INFO,
              message: action.payload['message']
            },
            loading: false
          });

        default:
          return state;
    }
};

export const getIsAuthenticated = (authenticationState: IAuthenticationDataState): boolean => authenticationState.isAuthenticated;
export const getIsNewRegisterUser = (authenticationState: IAuthenticationDataState): boolean => authenticationState.isNewRegisterUser;
export const getNewRegisterUserStatusChanged = (authenticationState: IAuthenticationDataState): boolean => authenticationState.isUserStatusChanged;
export const getJwtToken = (authenticationState: IAuthenticationDataState): string => authenticationState.jwtToken;
export const getStatus = (authenticationState: IAuthenticationDataState): IMessage => authenticationState.status;
export const getAccount = (authenticationState: IAuthenticationDataState): IAccount => authenticationState.account;
export const getIsMFAEnabled = (authenticationState: IAuthenticationDataState): boolean => authenticationState.isMFAEnabled;
export const getIsMFAAuthenticated = (authenticationState: IAuthenticationDataState): boolean => authenticationState.isMFAAuthenticated;
export const getMFAAuthSetup = (authenticationState: IAuthenticationDataState): IMfaAuthSetup => authenticationState.MFAAuthSetup;
export const getMFAAuthCodeType = (authenticationState: IAuthenticationDataState): IMfaAuthCodeType => authenticationState.MFAAuthCodeType;

export const getStatusIfUserStatusNotChanged = createSelector(getNewRegisterUserStatusChanged, getStatus, (isStatusChanged, status: IMessage): IMessage =>
  (!isStatusChanged) ? status : null
);

export const getSubPortalName = createSelector(getIsAuthenticated, getAccount, (isAuthenticated:boolean, account: IAccount): string =>
(isAuthenticated) ? account.subPortal : ''
);



// WEBPACK FOOTER //
// ./src/app/stateManagement/reducers/data/authentication.reducer.ts